<script setup lang="ts">
import {
  adminDeleteWebhookEndpoint,
  adminGetWebhookEndpoint,
  adminToggleWebhookEndpointActive,
  getAdminListWebhookEndpointsQueryKey,
  useAdminListWebhookEndpoints,
} from '@swipe4work/api-orval'
import type { ListWebhookEndpointsWebhookEndpointDto } from '@swipe4work/models-orval'
import { WebhookTrigger } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useConfirm } from 'primevue/useconfirm'
import CreateWebhookEndpointDialog from '@/features/settings/dialogs/CreateWebhookEndpointDialog.vue'
import UpdateWebhookEndpointDialog from '@/features/settings/dialogs/UpdateWebhookEndpointDialog.vue'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useToast } from '@/shared/composables/UseToast'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'

definePage({
  path: '/settings/Webhooks',
  name: '/settings/Webhooks',
  meta: {
    title: 'Webhooks',
    permission: {
      resource: 'Webhooks',
      level: 'Read',
    },
  },
})

const toast = useToast()
const confirm = useConfirm()
const dialog = useDynamicDialog()
const queryClient = useQueryClient()

const { data, isPending, isError } = useAdminListWebhookEndpoints()
const endpoints = computed(() => data.value?.endpoints ?? [])

const triggerLabels: Record<WebhookTrigger, string> = {
  [WebhookTrigger.CompanyCreated]: 'Bedrijf aangemaakt',
  [WebhookTrigger.CompanyExactAssigned]: 'Exact Online gekoppeld',
}

function openCreateDialog() {
  dialog.open(CreateWebhookEndpointDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto', 
      },
    },
  })
}

async function openEditDialog(endpoint: ListWebhookEndpointsWebhookEndpointDto) {
  try {
    const detail = await adminGetWebhookEndpoint(endpoint.id)

    dialog.open(UpdateWebhookEndpointDialog, {
      props: {
        modal: true,
        closable: true,
        showHeader: false,
        dismissableMask: true,
        style: {
          width: 'auto',
          height: 'auto', 
        },
      },
      data: { endpoint: detail },
    })
  }
  catch {
    toast.error('Fout', 'Kon de endpoint details niet ophalen.')
  }
}

async function toggleActive(endpoint: ListWebhookEndpointsWebhookEndpointDto) {
  try {
    await adminToggleWebhookEndpointActive(endpoint.id)
    await queryClient.invalidateQueries({ queryKey: getAdminListWebhookEndpointsQueryKey() })
    toast.success(
      endpoint.isActive ? 'Gedeactiveerd' : 'Geactiveerd',
      `Endpoint is ${endpoint.isActive ? 'gedeactiveerd' : 'geactiveerd'}.`,
    )
  }
  catch {
    toast.error('Fout', 'Kon de status niet wijzigen.')
  }
}

function confirmDelete(endpoint: ListWebhookEndpointsWebhookEndpointDto) {
  confirm.require({
    message: `Wil je het endpoint "${endpoint.url}" verwijderen?`,
    header: 'Bevestigen',
    icon: 'fa-regular fa-trash',
    rejectLabel: 'Annuleer',
    acceptLabel: 'Verwijder',
    acceptClass: 'p-button-danger',
    accept: () => deleteEndpoint(endpoint),
  })
}

async function deleteEndpoint(endpoint: ListWebhookEndpointsWebhookEndpointDto) {
  try {
    await adminDeleteWebhookEndpoint(endpoint.id)
    await queryClient.invalidateQueries({ queryKey: getAdminListWebhookEndpointsQueryKey() })
    toast.success('Verwijderd', 'Het endpoint is verwijderd.')
  }
  catch {
    toast.error('Fout', 'Kon het endpoint niet verwijderen.')
  }
}
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <Card>
      <template #content>
        <div v-if="isPending" class="flex flex-col gap-2">
          <Skeleton v-for="n in 3" :key="n" height="3rem" />
        </div>

        <Message
          v-else-if="isError"
          severity="error"
          :closable="false"
        >
          Er is een fout opgetreden bij het laden van de webhooks.
        </Message>

        <div v-else class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <h2 class="text-lg font-semibold">
              Webhook endpoints
            </h2>
            
            <Button
              v-permission="{ resource: AdminPermissionResource.Webhooks, level: PermissionLevel.Write }"
              label="Nieuw endpoint"
              icon="fa-regular fa-plus"
              @click="openCreateDialog"
            />
          </div>

          <DataTable          
            :value="endpoints"
            :rows="20"
            paginator
            row-hover
            class="w-full"
          >
            <template #empty>
              <p class="text-center text-surface-500">
                Geen webhook endpoints geconfigureerd.
              </p>
            </template>

            <Column field="url" header="URL">
              <template #body="{ data: row }: { data: ListWebhookEndpointsWebhookEndpointDto }">
                <span class="break-all text-sm font-mono">{{ row.url }}</span>
              </template>
            </Column>

            <Column field="description" header="Omschrijving">
              <template #body="{ data: row }: { data: ListWebhookEndpointsWebhookEndpointDto }">
                <span class="text-surface-500">{{ row.description || '—' }}</span>
              </template>
            </Column>

            <Column field="triggers" header="Triggers">
              <template #body="{ data: row }: { data: ListWebhookEndpointsWebhookEndpointDto }">
                <div class="flex flex-wrap gap-1">
                  <Tag
                    v-for="trigger in row.triggers"
                    :key="trigger"
                    :value="triggerLabels[trigger] ?? trigger"
                    severity="secondary"
                  />
                </div>
              </template>
            </Column>

            <Column field="isActive" header="Status" style="width: 8rem">
              <template #body="{ data: row }: { data: ListWebhookEndpointsWebhookEndpointDto }">
                <Tag
                  :value="row.isActive ? 'Actief' : 'Inactief'"
                  :severity="row.isActive ? 'success' : 'secondary'"
                />
              </template>
            </Column>

            <Column header="" style="width: 10rem">
              <template #body="{ data: row }: { data: ListWebhookEndpointsWebhookEndpointDto }">
                <div class="flex justify-end gap-1">
                  <Button
                    v-permission="{ resource: AdminPermissionResource.Webhooks, level: PermissionLevel.Write }"
                    v-tooltip.top="'Bewerken'"
                    icon="fa-regular fa-pen"
                    severity="secondary"
                    text
                    rounded
                    size="small"
                    @click="openEditDialog(row)"
                  />
                  <Button
                    v-permission="{ resource: AdminPermissionResource.Webhooks, level: PermissionLevel.Write }"
                    v-tooltip.top="row.isActive ? 'Deactiveren' : 'Activeren'"
                    :icon="row.isActive ? 'fa-regular fa-toggle-on' : 'fa-regular fa-toggle-off'"
                    severity="secondary"
                    text
                    rounded
                    size="small"
                    @click="toggleActive(row)"
                  />
                  <Button
                    v-permission="{ resource: AdminPermissionResource.Webhooks, level: PermissionLevel.Delete }"
                    v-tooltip.top="'Verwijderen'"
                    icon="fa-regular fa-trash"
                    severity="danger"
                    text
                    rounded
                    size="small"
                    @click="confirmDelete(row)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <ConfirmDialog />
  </div>
</template>
